import React from "react";

/**
 * THE PACKAGE, DRAWN AS A CARD (user, 2026-10-09).
 *
 * The same card the purchase email draws, so the thing a customer sees on screen and the thing they
 * received are recognisably the SAME OBJECT. A package is not a payment card and the copy never pretends
 * otherwise — what it borrows is the part a card does well: it says "this is yours, here is its number, and
 * here is when it runs out" at a glance, instead of in a paragraph.
 *
 * WHY THE NUMBER IS GROUPED 4-4-4-4. Sixteen digits in a row are miscounted when they are read back off a
 * screen, and this is the number a customer has to type to use the package or to give it away. The grouping
 * is display only: the stored and compared value is bare, and this never feeds a comparison.
 *
 * WHY IT DEGRADES HONESTLY. A package bought before numbers existed has none, and a purchase being viewed
 * by somebody who does not hold it may have had it rotated away. Neither case gets an invented number — the
 * card simply renders without the number band, because a placeholder would look like a real number.
 */
export default function PackageCard({
  accountNumber,
  holderName,
  validUntil,
  totalSessions,
  shopName,
  packageName,
  status,
}) {
  const digits = String(accountNumber || "").replace(/\D/g, "");
  const grouped = digits.replace(/(\d{4})(?=\d)/g, "$1 ");

  const sessionsLabel =
    totalSessions === undefined || totalSessions === null
      ? null
      : `${totalSessions} session${Number(totalSessions) === 1 ? "" : "s"}`;

  const expiry = validUntil ? new Date(validUntil) : null;
  /**
   * AN EXPIRED PACKAGE SAYS SO (reported 2026-10-19: *"when validity date is in the past, the system should
   * display expired and the date in red"*). "Valid until 18 October 2026" printed in white on a date that had
   * already gone is the card telling the customer something untrue. The status chip changes with it, because
   * `active` is the stored status until the nightly sweep runs — the DATE is the fact, so the date decides the
   * label.
   */
  const isExpired = Boolean(expiry && !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now());
  const expiryLabel =
    expiry && !Number.isNaN(expiry.getTime())
      ? expiry.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : null;

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #24325a 0%, #1e2a4a 55%, #16203a 100%)",
        borderRadius: "14px",
        padding: "18px 20px",
        color: "#ffffff",
        fontFamily: "Inter, sans-serif",
        marginBottom: "14px",
        // CARD PROPORTIONS. A card is a landscape rectangle about 1.6 to 1, and a full-width block with the
        // same contents reads as a panel rather than as a card. Capped rather than stretched, and centred.
        width: "100%",
        maxWidth: "430px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            color: "#c7d2fe",
            fontSize: "11px",
            letterSpacing: "3px",
            fontWeight: "bold",
          }}
        >
          OGESTYLE
        </span>
        <span
          style={{
            color: "#c7d2fe",
            fontSize: "11px",
            letterSpacing: "2px",
            textTransform: "uppercase",
          }}
        >
          Package{isExpired ? " · expired" : status ? ` · ${status}` : ""}
        </span>
      </div>

      {/* THE CHIP. Drawn with CSS rather than an image, so it cannot fail to load and needs no asset. It is
          there because a card without one does not read as a card - and this whole object exists to be
          recognised as one at a glance. */}
      <div
        aria-hidden="true"
        style={{
          width: "42px",
          height: "30px",
          marginTop: "14px",
          borderRadius: "5px",
          background:
            "linear-gradient(135deg, #f0d68a 0%, #d9b45b 40%, #b8933d 100%)",
          border: "1px solid #a8842f",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* The contact grid a real chip has: two vertical dividers and one horizontal, offset so the pads do
            not look like a plain grid. */}
        <span
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "33%",
            width: "1px",
            background: "#a8842f",
            opacity: 0.75,
          }}
        />
        <span
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "66%",
            width: "1px",
            background: "#a8842f",
            opacity: 0.75,
          }}
        />
        <span
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            height: "1px",
            background: "#a8842f",
            opacity: 0.75,
          }}
        />
      </div>

      {grouped ? (
        <>
          <div
            style={{
              marginTop: "16px",
              fontSize: "21px",
              fontWeight: "bold",
              letterSpacing: "4px",
              fontFamily: "'Courier New', Courier, monospace",
            }}
          >
            {grouped}
          </div>
          <div
            style={{
              color: "#93a4d4",
              fontSize: "11px",
              letterSpacing: "1px",
              marginTop: "4px",
            }}
          >
            Your package number
          </div>
        </>
      ) : null}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginTop: "18px",
        }}
      >
        <div>
          <div
            style={{
              color: "#93a4d4",
              fontSize: "9px",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Cardholder
          </div>
          <div style={{ fontSize: "14px", fontWeight: "bold", marginTop: "3px" }}>
            {holderName || "—"}
          </div>
        </div>
        {expiryLabel ? (
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                color: isExpired ? "#fca5a5" : "#93a4d4",
                fontSize: "9px",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              {isExpired ? "Expired" : "Valid until"}
            </div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "3px",
                color: isExpired ? "#fca5a5" : "inherit",
              }}
            >
              {expiryLabel}
            </div>
          </div>
        ) : null}
      </div>

      {(sessionsLabel || shopName || packageName) && (
        <div
          style={{
            borderTop: "1px solid #3b4a75",
            marginTop: "14px",
            paddingTop: "12px",
            color: "#93a4d4",
            fontSize: "12px",
            lineHeight: 1.6,
          }}
        >
          {[sessionsLabel, shopName, packageName].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}
